import axios from "axios";
import router from "@/router";
import { constants } from "../constant/constants";
import EnvironmentVariables from "@/EnvironmentVariables";
import { AuthType, authService } from "./auth-service";
import {
    createSignInProof,
    isMagicLinkBindingEnabled,
    magicLinkAuthorization,
} from "./dpop/dpop-magic-link";
import { clearDpopState } from "./dpop/dpop-sign-out";

// Define the API client instance
const apiClient = axios.create({
    baseURL: EnvironmentVariables.getApimUrl(),
    headers: {
        "Content-Type": "application/json",
    },
    withCredentials: true, // Important: include cookies with every request
    xsrfCookieName: "XSRF-TOKEN",
    xsrfHeaderName: "X-XSRF-TOKEN",
});

apiClient.interceptors.request.use(async (config) => {
    const token = localStorage.getItem(
        constants.localStorage.keys.magicLink.accessToken,
    );
    if (token) {
        // A bound token (DPoP, see dpop-magic-link) goes as `DPoP <token>` with a proof; refresh needs it.
        const auth = await magicLinkAuthorization(
            config.method,
            apiClient.getUri(config),
            token,
        );
        config.headers.Authorization = auth.Authorization;
        if (auth.DPoP) config.headers.DPoP = auth.DPoP;
    }
    return config;
});

// Store tokens in localStorage
const storeTokens = (data: { AccessToken: string; ExpiresAt: string }) => {
    localStorage.setItem(
        constants.localStorage.keys.magicLink.accessToken,
        data.AccessToken,
    );
    localStorage.setItem(
        constants.localStorage.keys.magicLink.tokenExpiry,
        data.ExpiresAt,
    );
};

const magicLinkAuthService = {
    /**
     * Request a magic link to be sent to the user's email
     * @param {string} email - The user's email address
     * @param {string} redirectUrl - URL to redirect to after verification
     * @returns {Promise} - Response from the API
     */
    requestMagicLink(email: string, redirectUrl: string) {
        const openApiClient = axios.create({
            baseURL: EnvironmentVariables.getApimUrl(),
            headers: {
                "Content-Type": "application/json",
            },
        });

        return openApiClient.post("/api/magic-link/request", {
            email,
            redirectUrl: redirectUrl || window.location.origin + "/ticketAdd",
        });
    },

    /**
     * Verify a magic link token
     * @param {string} token - The token from the magic link
     * @returns {Promise} - Response from the API with tokens
     */
    async verifyMagicLink(token: string) {
        const apimUrl = EnvironmentVariables.getApimUrl();
        const openApiClient = axios.create({
            baseURL: apimUrl,
            headers: {
                "Content-Type": "application/json",
            },
            withCredentials: true,
        });

        // With DPoP for magic link on, prove possession of this browser's key so the gateway binds the session's
        // tokens to it. Without a proof the gateway issues an unbound token, exactly as before.
        const headers: Record<string, string> = {};
        if (isMagicLinkBindingEnabled()) {
            try {
                headers.DPoP = await createSignInProof(
                    "POST",
                    `${apimUrl.replace(/\/+$/, "")}/api/magic-link/verify`,
                );
            } catch (error) {
                console.warn("DPoP proof unavailable; signing in unbound.", error);
            }
        }

        return openApiClient
            .post("/api/magic-link/verify", { token }, { headers })
            .then((response) => {
                console.log("Auth service verify response:", response.data);
                // Store the access token and expiry
                storeTokens(response.data);
                authService.setAuthType(AuthType.MagicLink);
                return response;
            });
    },

    /**
     * Refresh the access token using the HttpOnly refresh token cookie
     * @returns {Promise} - Response from the API with new access token
     */
    refreshToken() {
        return apiClient.post("/api/magic-link/refresh", {});
    },

    /**
     * Log the user out
     * @returns {Promise} - Response from the API
     */
    async logout() {
        try {
            // Clear session on the server side (e.g., by calling logout endpoint)
            await apiClient.post("/api/magic-link/logout");
        } catch (error) {
            console.error("Error during logout:", error);
        } finally {
            // Clear authentication locally and redirect to login
            await this.clearAuth();
            await router.push("/");
        }
    },

    /**
     * Clear authentication data from localStorage
     */
    clearAuth() {
        // The session is over, so its DPoP key goes too (best effort, never waited on).
        void clearDpopState();
        localStorage.removeItem(constants.localStorage.keys.magicLink.accessToken);
        localStorage.removeItem(constants.localStorage.keys.magicLink.tokenExpiry);
        localStorage.removeItem(constants.localStorage.keys.authType);
    },

    /**
     * Check if the user is authenticated
     * @returns {boolean} - True if the user is authenticated
     */
    isAuthenticated() {
        const token = localStorage.getItem(
            constants.localStorage.keys.magicLink.accessToken,
        );
        const expiry = localStorage.getItem(
            constants.localStorage.keys.magicLink.tokenExpiry,
        );

        if (!token || !expiry) {
            return false;
        }

        // Check if token is expired
        const expiryDate = new Date(expiry);
        const now = new Date();
        return expiryDate > now;
    },

    /**
     * Get the authenticated user's information from the JWT token
     * @returns {Object|null} - User information or null if not authenticated
     */
    getAuthUser() {
        if (!this.isAuthenticated()) {
            return null;
        }

        const token =
            localStorage.getItem(constants.localStorage.keys.magicLink.accessToken) ||
            "";
        const base64Url = token.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split("")
                .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
                .join(""),
        );

        return JSON.parse(jsonPayload);
    },

    async getAccessToken() {
        try {
            // No need to send the refresh token since it's in the HttpOnly cookie
            // Just call the refresh endpoint and the server will use the HttpOnly cookie
            const response = await this.refreshToken();

            if (response?.data?.AccessToken) {
                // Store the new access token
                localStorage.setItem(
                    constants.localStorage.keys.magicLink.accessToken,
                    response.data.AccessToken,
                );
                // Don't need to store refresh token since it's now in HttpOnly cookie
                // Note: The server puts refreshToken in a cookie, but still returns it in the response
                localStorage.setItem(
                    constants.localStorage.keys.magicLink.tokenExpiry,
                    response.data.ExpiresAt,
                );

                return response.data.AccessToken as string;
            } else {
                const accessTokenEmptyError = new Error(
                    "Access token was empty when acquiring access token",
                );
                throw accessTokenEmptyError;
            }
        } catch (refreshError) {
            console.error("Error in refresing token: ");
            console.error(refreshError);
            this.logErrorInLocalStorage(refreshError, "getAccessToken");
            // If refresh fails, redirect to login
            this.clearAuth();
            router.push("/");
            return null;
        }
    },

    logErrorInLocalStorage(error: any, sourceMethod: string = "") {
        sourceMethod = `msal-service: ${sourceMethod}`;
        let errorInString: string = "Logging user out after error";
        try {
            if (error != null) {
                errorInString = error.toString();
            }
        } catch (e) {
            console.error("Error in serializing error object");
        }

        errorInString = `${sourceMethod} : ${errorInString}`;

        localStorage.setItem("magic-link-auth-error", errorInString);
    },

    getAccessTokenFromLocalStorage() {
        return localStorage.getItem(
            constants.localStorage.keys.magicLink.accessToken,
        );
    },

    async signIn() {
        let isAuthenticated: boolean = this.isAuthenticated();
        if (!isAuthenticated) {
            await this.getAccessToken();
        }
    },
};

export default magicLinkAuthService;
